import {
  TabsContent,
  FormField,
  SearchableSelect,
} from '@/components/molecules';
import { Paragraph, Button, Input, Checkbox } from '@/components/atoms';
import { useForm, Controller } from 'react-hook-form';
import { useMemo, useState, useEffect } from 'react';
import {
  useBanks,
  useBusiness,
  useUser,
  useResolveAccount,
  useRequestSettlementAccountUpdate,
  useConfirmSettlementAccountUpdate,
} from '@/api';
import { useDebounce } from '@/hooks/use-debounce';
import toast from 'react-hot-toast';
import type { BusinessSettlementAccount } from '@/types/business';
import { SettlementAccountOTPDialog } from '../settlement-account-otp-dialog';

interface BankAccountFormData {
  bankName: string;
  accountNumber: string;
  accountName: string;
  transactionFeeAgreement: boolean;
}

export const BankInfoTab = () => {
  const [isValidatingAccount, setIsValidatingAccount] = useState(false);
  const [accountValidated, setAccountValidated] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isOTPDialogOpen, setIsOTPDialogOpen] = useState(false);
  const [verifiedAccountName, setVerifiedAccountName] = useState('');
  const [pendingPayload, setPendingPayload] = useState<{
    accountNumber: string;
    bankCode: string;
    bankName: string;
  } | null>(null);

  const { data: banks, isLoading: isLoadingBanks } = useBanks();
  const { data: business } = useBusiness();
  const { data: user } = useUser('me');
  const { mutate: resolveAccount } = useResolveAccount();

  const { mutate: requestOtp, isPending: isRequestingOtp } =
    useRequestSettlementAccountUpdate((response) => {
      setVerifiedAccountName(response.data.accountName);
      setIsOTPDialogOpen(true);
    });

  const { mutate: confirmOtp, isPending: isConfirmingOtp } =
    useConfirmSettlementAccountUpdate(() => {
      setIsOTPDialogOpen(false);
      setIsEditing(false);
      setPendingPayload(null);
      setVerifiedAccountName('');
    });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    setValue,
    watch,
    control,
    setError,
    clearErrors,
    reset,
  } = useForm<BankAccountFormData>({
    defaultValues: {
      bankName: '',
      accountNumber: '',
      accountName: '',
      transactionFeeAgreement: false,
    },
    mode: 'onChange',
  });

  const accountNumber = watch('accountNumber');
  const bankName = watch('bankName');
  const accountName = watch('accountName');
  const transactionFeeAgreement = watch('transactionFeeAgreement');
  const debouncedAccountNumber = useDebounce(accountNumber, 2000);

  const bankOptions = useMemo(
    () =>
      banks?.map((bank) => ({
        value: bank.code,
        label: bank.name,
      })) ?? [],
    [banks]
  );

  const existingSettlement = business?.settlementAccount as
    | BusinessSettlementAccount
    | undefined
    | null;

  const hasExistingBankDetails = Boolean(existingSettlement?.accountNumber);
  const fieldsLocked = hasExistingBankDetails && !isEditing;

  useEffect(() => {
    if (business?.settlementAccount && banks) {
      const { accountNumber, bankCode, accountName } =
        business.settlementAccount as BusinessSettlementAccount;
      const bank = banks.find((b) => b.code === bankCode);

      reset({
        accountNumber: accountNumber || '',
        accountName: accountName || '',
        bankName: bank?.code || '',
        transactionFeeAgreement: true,
      });
      setAccountValidated(!!accountName);
      setIsEditing(false);
    }
  }, [business, banks, reset]);

  useEffect(() => {
    if (fieldsLocked) return;

    if (
      debouncedAccountNumber &&
      debouncedAccountNumber.length === 10 &&
      bankName &&
      isDirty
    ) {
      setValue('accountName', '', { shouldDirty: false });
      setIsValidatingAccount(true);
      setAccountValidated(false);
      clearErrors('accountNumber');

      resolveAccount(
        {
          accountNumber: debouncedAccountNumber,
          bankCode: bankName,
        },
        {
          onSuccess: (data) => {
            setValue('accountName', data.account_name, { shouldDirty: false });
            setIsValidatingAccount(false);
            setAccountValidated(true);
          },
          onError: () => {
            const message =
              'We couldn’t validate this account number. Please confirm the number and bank and try again.';

            setValue('accountName', '', { shouldDirty: false });
            setIsValidatingAccount(false);
            setAccountValidated(false);
            setError('accountNumber', { type: 'manual', message });
            toast.error(message);
          },
        }
      );
    } else if (
      debouncedAccountNumber &&
      debouncedAccountNumber.length < 10 &&
      isDirty
    ) {
      setValue('accountName', '', { shouldDirty: false });
      setAccountValidated(false);
      clearErrors('accountNumber');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAccountNumber, isDirty, fieldsLocked]);

  useEffect(() => {
    if (fieldsLocked) return;

    if (accountNumber && accountNumber.length === 10 && bankName && isDirty) {
      setValue('accountName', '', { shouldDirty: false });
      setAccountValidated(false);
    }
  }, [bankName, accountNumber, setValue, isDirty, fieldsLocked]);

  const sendOtpForPendingAccount = (
    payload: {
      accountNumber: string;
      bankCode: string;
      bankName: string;
    },
    options?: { keepDialogOpen?: boolean }
  ) => {
    setPendingPayload(payload);
    requestOtp(
      {
        accountNumber: payload.accountNumber,
        bankCode: payload.bankCode,
      },
      {
        onError: () => {
          if (!options?.keepDialogOpen) {
            setIsOTPDialogOpen(false);
          }
        },
      }
    );
  };

  const onSubmit = (data: BankAccountFormData) => {
    if (!data.transactionFeeAgreement) return;

    const selectedBank = banks?.find((bank) => bank.code === data.bankName);
    if (!selectedBank) {
      toast.error('Please select a bank');
      return;
    }

    sendOtpForPendingAccount({
      accountNumber: data.accountNumber,
      bankCode: selectedBank.code,
      bankName: selectedBank.name,
    });
  };

  const handleCancelEdit = () => {
    if (existingSettlement && banks) {
      const bank = banks.find((b) => b.code === existingSettlement.bankCode);
      reset({
        accountNumber: existingSettlement.accountNumber || '',
        accountName: existingSettlement.accountName || '',
        bankName: bank?.code || '',
        transactionFeeAgreement: true,
      });
      setAccountValidated(!!existingSettlement.accountName);
    }
    setIsEditing(false);
    setPendingPayload(null);
    setVerifiedAccountName('');
  };

  const handleConfirmOtp = (code: string) => {
    if (!pendingPayload) return;
    confirmOtp({
      accountNumber: pendingPayload.accountNumber,
      bankCode: pendingPayload.bankCode,
      bankName: pendingPayload.bankName,
      code,
    });
  };

  const handleResendOtp = () => {
    if (!pendingPayload) return;
    sendOtpForPendingAccount(pendingPayload, { keepDialogOpen: true });
  };

  return (
    <TabsContent value="bank-info">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-6 max-w-xl mx-auto py-6"
      >
        <Paragraph size="sm" className="text-gray-500 font-medium">
          Add the payout account where your earnings will be sent. Ensure the
          details match your verified business or personal identity to avoid
          payout delays or failures. Changing your bank account requires a code
          sent to your login email.
        </Paragraph>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <FormField
              label="Bank name"
              className="w-full"
              error={errors.bankName?.message}
            >
              <Controller
                name="bankName"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    value={field.value}
                    onValueChange={field.onChange}
                    options={bankOptions}
                    disabled={isLoadingBanks || fieldsLocked}
                    placeholder={
                      isLoadingBanks ? 'Loading banks...' : 'Select a bank'
                    }
                    searchPlaceholder="Search banks..."
                    emptyMessage="No banks match your search."
                  />
                )}
              />
            </FormField>

            <div className="grid grid-cols-2 gap-4 w-full">
              <FormField
                label="Account no"
                className="w-full"
                error={errors.accountNumber?.message}
              >
                <Input
                  placeholder="0000000000"
                  {...register('accountNumber')}
                  maxLength={10}
                  disabled={fieldsLocked}
                />
              </FormField>

              <FormField
                label="Account name"
                className="w-full"
                error={errors.accountName?.message}
              >
                <Input
                  placeholder={
                    isValidatingAccount
                      ? 'Validating account...'
                      : 'Account name'
                  }
                  {...register('accountName')}
                  disabled
                />
              </FormField>
            </div>
          </div>

          <div className="space-y-3 rounded-md border border-border p-4">
            <Paragraph size="sm" className="font-medium">
              Transaction Fee Agreement
            </Paragraph>
            <label className="flex items-start gap-3 text-sm text-muted-foreground">
              <Controller
                name="transactionFeeAgreement"
                control={control}
                rules={{
                  validate: (value) =>
                    value ||
                    'You must agree to the transaction fee before saving your account details.',
                }}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) =>
                      field.onChange(checked === true)
                    }
                    disabled={fieldsLocked}
                  />
                )}
              />
              <span>
                I understand and agree that Sellyt will deduct a 2% platform fee
                from every completed sale/order
              </span>
            </label>
            {errors.transactionFeeAgreement?.message && (
              <Paragraph size="xs" className="text-destructive">
                {errors.transactionFeeAgreement.message}
              </Paragraph>
            )}
          </div>
        </div>

        {fieldsLocked ? (
          <Button
            className="w-full"
            label="Change bank account"
            size="lg"
            type="button"
            onClick={() => setIsEditing(true)}
          />
        ) : (
          <div className="flex flex-col gap-2">
            <Button
              className="w-full"
              label={
                hasExistingBankDetails
                  ? 'Send code to update bank account'
                  : 'Send code to save bank account'
              }
              size="lg"
              type="submit"
              isLoading={isRequestingOtp}
              disabled={
                !isDirty ||
                !isValid ||
                !accountValidated ||
                !transactionFeeAgreement
              }
            />
            {hasExistingBankDetails && (
              <Button
                className="w-full"
                variant="outline"
                label="Cancel"
                size="lg"
                type="button"
                onClick={handleCancelEdit}
                disabled={isRequestingOtp}
              />
            )}
          </div>
        )}
      </form>

      <SettlementAccountOTPDialog
        isOpen={isOTPDialogOpen}
        onClose={() => {
          setIsOTPDialogOpen(false);
        }}
        onConfirm={handleConfirmOtp}
        onResend={handleResendOtp}
        isConfirming={isConfirmingOtp}
        isResending={isRequestingOtp}
        accountName={verifiedAccountName || accountName}
        accountNumber={pendingPayload?.accountNumber || accountNumber}
        bankName={pendingPayload?.bankName || ''}
        email={user?.auth?.email}
      />
    </TabsContent>
  );
};
